import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { jobService, inventoryService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";
import { format, parseISO, differenceInDays } from "date-fns";
import EquipmentAssignment from "../components/EquipmentAssignment";
import CrewAssignment from "../components/CrewAssignment";
import JobNotepad from "../components/JobNotepad";
import Modal from "../components/Modal";
import JobEditForm from "../components/JobEditForm";
import DocumentForm from "../components/DocumentForm";
import type { JobEquipment, Document } from "../types/database";

// Location type for better organization (UK-based)
interface LocationDetails {
  name: string;
  address?: string;
  city?: string;
  state?: string; // Used for County in UK
  postcode?: string;
  country?: string;
  notes?: string;
}

export default function JobDetail() {
  const { orgId, jobId } = useParams();
  const navigate = useNavigate();
  const { currentOrganization, user } = useAuth();
  const [activeTab, setActiveTab] = useState<
    "details" | "equipment" | "crew" | "notepad" | "documents"
  >("details");
  const queryClient = useQueryClient();
  const [isEditJobModalOpen, setIsEditJobModalOpen] = useState(false);
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);

  // Fetch job data
  const {
    data: job,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => jobService.getJob(jobId!, currentOrganization?.id),
    enabled: !!jobId && !!currentOrganization?.id,
  });

  // Fetch job documents
  const { data: documents = [], refetch: refetchDocuments } = useQuery<
    Document[]
  >({
    queryKey: ["job-documents", jobId],
    queryFn: () =>
      inventoryService.getDocuments({
        jobId: jobId!,
        organizationId: currentOrganization?.id || "",
      }),
    enabled: !!jobId && !!currentOrganization?.id,
  });

  // Migration function to convert old string[] equipment to JobEquipment[]
  const migrateEquipmentData = (equipment: any): JobEquipment[] => {
    if (!Array.isArray(equipment)) {
      return [];
    }

    // If it's already in the new format, return as is
    if (
      equipment.length > 0 &&
      typeof equipment[0] === "object" &&
      "id" in equipment[0]
    ) {
      return equipment as JobEquipment[];
    }

    // Convert old string format to new format
    return equipment.map((item: string, index: number) => {
      // Parse quantity from string like "2x Speaker"
      const regex = /^(\d+)x\s+(.+)$/;
      const matches = item.match(regex);

      if (matches) {
        const quantity = parseInt(matches[1]) || 1;

        // For migrated data, we'll use the name as a temporary ID
        // In a real migration, you'd want to look up the actual inventory item ID
        return { id: `migrated-${index}`, quantity };
      }

      // If no quantity format, assume quantity of 1
      return { id: `migrated-${index}`, quantity: 1 };
    });
  };

  // Get migrated equipment data
  const equipmentData = job ? migrateEquipmentData(job.equipment) : [];

  // Mutation for updating job status
  const updateJobMutation = useMutation({
    mutationFn: (status: "upcoming" | "in_progress" | "completed") =>
      jobService.updateJob(jobId!, { status }, currentOrganization?.id || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["job", jobId] });
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });

  // Mutation for updating job equipment
  const updateEquipmentMutation = useMutation({
    mutationFn: (equipment: JobEquipment[]) =>
      jobService.updateJob(
        jobId!,
        { equipment },
        currentOrganization?.id || ""
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["job", jobId] });
    },
  });

  // Mutation for updating job crew
  const updateCrewMutation = useMutation({
    mutationFn: (crew: string[]) =>
      jobService.updateJob(jobId!, { crew }, currentOrganization?.id || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["job", jobId] });
    },
  });

  // Format date for display
  const formatDate = (dateString: string) => {
    return format(parseISO(dateString), "dd MMM yyyy");
  };

  // Handle status change
  const handleStatusChange = (
    status: "upcoming" | "in_progress" | "completed"
  ) => {
    if (job && job.status !== status) {
      updateJobMutation.mutate(status);
    }
  };

  // Handle equipment change
  const handleEquipmentChange = (equipment: JobEquipment[]) => {
    if (job) {
      updateEquipmentMutation.mutate(equipment);
    }
  };

  // Handle crew change
  const handleCrewChange = (crew: string[]) => {
    if (job) {
      updateCrewMutation.mutate(crew);
    }
  };

  // Handle document editing
  const openEditDocumentModal = (document: Document) => {
    setEditingDocument(document);
    setIsAddDocumentModalOpen(true);
  };

  // Parse location data if it's in JSON format
  const parseLocation = (locationString: string): LocationDetails => {
    try {
      return JSON.parse(locationString) as LocationDetails;
    } catch (e) {
      // If not JSON, treat as simple location name
      return { name: locationString };
    }
  };

  // Calculate job duration
  const calculateDuration = () => {
    if (!job) return null;

    const startDate = parseISO(job.start_date);
    const endDate = parseISO(job.end_date);
    const days = differenceInDays(endDate, startDate) + 1; // +1 to include both start and end days

    return days === 1 ? "1 day" : `${days} days`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">
          Error loading job details. Please try again later.
        </div>
      </div>
    );
  }

  // Get location display data
  const location = parseLocation(job.location);

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate(`/org/${orgId}/jobs`)}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <svg
            className="h-5 w-5 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
        </button>
        <div>
          <div className="flex items-center space-x-2">
            <h1 className="text-2xl font-bold text-gray-900">{job.title}</h1>
            <span
              className={`px-2 py-1 text-xs font-medium rounded-full ${
                job.status === "upcoming"
                  ? "bg-yellow-100 text-yellow-800"
                  : job.status === "in_progress"
                  ? "bg-blue-100 text-blue-800"
                  : "bg-green-100 text-green-800"
              }`}
            >
              {job.status === "in_progress"
                ? "In Progress"
                : job.status.charAt(0).toUpperCase() + job.status.slice(1)}
            </span>
          </div>
          <p className="text-sm text-gray-500">Project #{job.project_number}</p>
        </div>
        <div className="ml-auto flex space-x-2">
          {/* Status change buttons */}
          <div className="flex items-center space-x-2 mr-4">
            <span className="text-sm text-gray-500">Status:</span>
            <div className="flex space-x-1">
              <button
                onClick={() => handleStatusChange("upcoming")}
                className={`px-2 py-1 text-xs font-medium rounded ${
                  job.status === "upcoming"
                    ? "bg-yellow-100 text-yellow-800 border-2 border-yellow-300"
                    : "bg-gray-100 text-gray-600 hover:bg-yellow-50"
                }`}
                disabled={updateJobMutation.isPending}
              >
                Upcoming
              </button>
              <button
                onClick={() => handleStatusChange("in_progress")}
                className={`px-2 py-1 text-xs font-medium rounded ${
                  job.status === "in_progress"
                    ? "bg-blue-100 text-blue-800 border-2 border-blue-300"
                    : "bg-gray-100 text-gray-600 hover:bg-blue-50"
                }`}
                disabled={updateJobMutation.isPending}
              >
                In Progress
              </button>
              <button
                onClick={() => handleStatusChange("completed")}
                className={`px-2 py-1 text-xs font-medium rounded ${
                  job.status === "completed"
                    ? "bg-green-100 text-green-800 border-2 border-green-300"
                    : "bg-gray-100 text-gray-600 hover:bg-green-50"
                }`}
                disabled={updateJobMutation.isPending}
              >
                Completed
              </button>
            </div>
          </div>

          <button
            onClick={() => setIsEditJobModalOpen(true)}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Edit Job
          </button>
        </div>
      </div>

      {/* Job summary card */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left column - Key details */}
          <div className="space-y-4">
            <div>
              <h3 className="text-xs font-medium uppercase text-gray-500 mb-1">
                Client
              </h3>
              <p className="text-base font-medium text-gray-900">
                {job.client || "N/A"}
              </p>
            </div>

            <div>
              <h3 className="text-xs font-medium uppercase text-gray-500 mb-1">
                Duration
              </h3>
              <p className="text-base font-medium text-gray-900">
                {calculateDuration()}
              </p>
            </div>

            <div>
              <h3 className="text-xs font-medium uppercase text-gray-500 mb-1">
                Status
              </h3>
              <div className="flex items-center">
                <span
                  className={`inline-block w-2 h-2 rounded-full mr-2 ${
                    job.status === "upcoming"
                      ? "bg-yellow-500"
                      : job.status === "in_progress"
                      ? "bg-blue-500"
                      : "bg-green-500"
                  }`}
                ></span>
                <span className="text-base font-medium text-gray-900">
                  {job.status === "in_progress"
                    ? "In Progress"
                    : job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                </span>
              </div>
            </div>
          </div>

          {/* Middle column - Location */}
          <div>
            <h3 className="text-xs font-medium uppercase text-gray-500 mb-1">
              Location
            </h3>
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="font-medium text-gray-900">{location.name}</p>
              {location.address && (
                <p className="text-sm text-gray-700 mt-1">{location.address}</p>
              )}
              {(location.city || location.state || location.postcode) && (
                <p className="text-sm text-gray-700">
                  {[location.city, location.state, location.postcode]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              )}
              {location.country && (
                <p className="text-sm text-gray-700">{location.country}</p>
              )}
              {location.notes && (
                <p className="text-xs text-gray-500 mt-2 border-t border-gray-200 pt-2">
                  {location.notes}
                </p>
              )}
            </div>
          </div>

          {/* Right column - Dates */}
          <div className="space-y-4">
            <div>
              <h3 className="text-xs font-medium uppercase text-gray-500 mb-1">
                Created
              </h3>
              <p className="text-sm text-gray-700">
                {formatDate(job.created_at)} by{" "}
                {job.created_by === user?.id ? "You" : job.created_by}
              </p>
            </div>

            <div>
              <h3 className="text-xs font-medium uppercase text-gray-500 mb-1">
                Last Updated
              </h3>
              <p className="text-sm text-gray-700">
                {formatDate(job.updated_at)} by{" "}
                {job.updated_by === user?.id ? "You" : job.updated_by}
              </p>
            </div>

            <div>
              <h3 className="text-xs font-medium uppercase text-gray-500 mb-1">
                Event Dates
              </h3>
              <p className="text-sm text-gray-700">
                {formatDate(job.start_date)} - {formatDate(job.end_date)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("details")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "details"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab("equipment")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "equipment"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Equipment
          </button>
          <button
            onClick={() => setActiveTab("crew")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "crew"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Crew
          </button>
          <button
            onClick={() => setActiveTab("notepad")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "notepad"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Notepad
          </button>
          <button
            onClick={() => setActiveTab("documents")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "documents"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Documents
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        {activeTab === "details" && (
          <div className="p-6 space-y-6">
            {/* Notes Section */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Notes</h2>
              <div className="bg-gray-50 p-4 rounded-lg">
                {job.description ? (
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {job.description}
                  </p>
                ) : (
                  <p className="text-gray-500 italic">
                    No notes provided for this job.
                  </p>
                )}
              </div>
            </div>

            {/* Timeline */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Timeline
              </h2>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex flex-wrap gap-4">
                  {/* Prep Date */}
                  <div className="bg-white p-4 rounded-lg shadow-sm flex-1 min-w-[200px]">
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center mr-2">
                        <svg
                          className="w-4 h-4 text-yellow-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                      </div>
                      <h3 className="text-base font-medium text-gray-900">
                        Prep Date
                      </h3>
                    </div>
                    <p className="text-sm text-gray-700 font-medium">
                      {formatDate(job.prep_date)}
                    </p>
                  </div>

                  {/* Ship Date */}
                  <div className="bg-white p-4 rounded-lg shadow-sm flex-1 min-w-[200px]">
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                        <svg
                          className="w-4 h-4 text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <h3 className="text-base font-medium text-gray-900">
                        Ship Date
                      </h3>
                    </div>
                    <p className="text-sm text-gray-700 font-medium">
                      {formatDate(job.ship_date)}
                    </p>
                  </div>

                  {/* Event Start */}
                  <div className="bg-white p-4 rounded-lg shadow-sm flex-1 min-w-[200px]">
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-2">
                        <svg
                          className="w-4 h-4 text-green-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                      </div>
                      <h3 className="text-base font-medium text-gray-900">
                        Event Start
                      </h3>
                    </div>
                    <p className="text-sm text-gray-700 font-medium">
                      {formatDate(job.start_date)}
                    </p>
                  </div>

                  {/* Event End */}
                  <div className="bg-white p-4 rounded-lg shadow-sm flex-1 min-w-[200px]">
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mr-2">
                        <svg
                          className="w-4 h-4 text-red-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                          />
                        </svg>
                      </div>
                      <h3 className="text-base font-medium text-gray-900">
                        Event End
                      </h3>
                    </div>
                    <p className="text-sm text-gray-700 font-medium">
                      {formatDate(job.end_date)}
                    </p>
                  </div>

                  {/* Return Date */}
                  <div className="bg-white p-4 rounded-lg shadow-sm flex-1 min-w-[200px]">
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-2">
                        <svg
                          className="w-4 h-4 text-purple-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z"
                          />
                        </svg>
                      </div>
                      <h3 className="text-base font-medium text-gray-900">
                        Return Date
                      </h3>
                    </div>
                    <p className="text-sm text-gray-700 font-medium">
                      {formatDate(job.return_date)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "equipment" && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">
                Equipment List
              </h2>
              {updateEquipmentMutation.isPending && (
                <span className="text-sm text-gray-500">Saving changes...</span>
              )}
            </div>

            <EquipmentAssignment
              selectedEquipment={equipmentData}
              onEquipmentChange={handleEquipmentChange}
              jobId={jobId}
            />
          </div>
        )}

        {activeTab === "crew" && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">
                Crew Members
              </h2>
              {updateCrewMutation.isPending && (
                <span className="text-sm text-gray-500">Saving changes...</span>
              )}
            </div>

            <CrewAssignment
              selectedCrew={job.crew || []}
              onCrewChange={handleCrewChange}
              jobId={jobId!}
            />
          </div>
        )}

        {activeTab === "notepad" && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">
                Shared Notes
              </h2>
            </div>

            <JobNotepad
              jobId={jobId!}
              initialContent={job.notes || ""}
              organizationId={currentOrganization?.id || ""}
            />
          </div>
        )}

        {activeTab === "documents" && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">Documents</h2>
              <button
                className="px-3 py-1 bg-primary text-white text-sm rounded-md hover:bg-primary/90"
                onClick={() => setIsAddDocumentModalOpen(true)}
              >
                Add Document
              </button>
            </div>

            {documents.length > 0 ? (
              <div className="space-y-3">
                {documents.map((doc: Document) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center mr-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-gray-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <div className="text-sm font-medium">{doc.name}</div>
                        <div className="text-xs text-gray-500">
                          {doc.type.charAt(0).toUpperCase() + doc.type.slice(1)}
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2 py-1 text-xs text-primary border border-primary rounded hover:bg-primary/5"
                      >
                        View
                      </a>
                      <button
                        onClick={() => openEditDocumentModal(doc)}
                        className="px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-100"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">
                  No documents available for this job
                </p>
                <button
                  onClick={() => setIsAddDocumentModalOpen(true)}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
                >
                  Add Your First Document
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Job Modal */}
      <Modal
        isOpen={isEditJobModalOpen}
        onClose={() => setIsEditJobModalOpen(false)}
        title="Edit Job"
        size="lg"
      >
        <JobEditForm
          job={job}
          onSuccess={() => {
            setIsEditJobModalOpen(false);
          }}
          onCancel={() => setIsEditJobModalOpen(false)}
        />
      </Modal>

      {/* Add/Edit Document Modal */}
      <Modal
        isOpen={isAddDocumentModalOpen}
        onClose={() => {
          setIsAddDocumentModalOpen(false);
          setEditingDocument(null);
        }}
        title={editingDocument ? "Edit Document" : "Add Document"}
        size="md"
      >
        <DocumentForm
          jobId={jobId!}
          document={editingDocument || undefined}
          onSuccess={() => {
            setIsAddDocumentModalOpen(false);
            setEditingDocument(null);
            refetchDocuments();
          }}
          onCancel={() => {
            setIsAddDocumentModalOpen(false);
            setEditingDocument(null);
          }}
        />
      </Modal>
    </div>
  );
}
